import {
  CustomButton,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomModal,
  CustomInput,
  CustomMaskInput,
  errorDialog,
  successDialog,
  Text,
  useText,
} from '@src/components';

import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Typography } from 'antd';
import { Header } from '@src/components/CustomDialog';
import { currentUserUpdate } from '@src/store/slice/userSlice';
import modalClose from '@src/assets/icons/icon-close.svg';
import modalWarning from '@src/assets/icons/icon-modal-warning.svg';
import { formMailRegex, formPhoneRegex } from '@src/utils/formRule';

const { Paragraph } = Typography;

const NewPhoneOrEmailFormModal = ({ modalVisible, handleModalVisible, user, currentUser }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [isOpenPopover, setIsOpenPopover] = useState(null);

  // 1. Move hook calls to the top level of the component
  const phonePlaceholder = useText('phoneNumber');
  const emailPlaceholder = useText('email');
  const headerTitle = `${useText('personalInfo')} - ${useText('edit')}`;

  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  useEffect(() => {
    if (modalVisible) {
      form.setFieldsValue({
        name: user?.name,
      });
    }

    return () => {
      setIsOpenPopover(null);
    };
  }, [modalVisible, form, user]);

  const handleClose = useCallback(() => {
    form.resetFields();
    handleModalVisible(false);
    setIsOpenPopover(null);
  }, [handleModalVisible, form, setIsOpenPopover]);

  const onFinishEdit = useCallback(
    async (values) => {
      onChangePopover(values?.name);

      if (isOpenPopover === false) {
        const currentMobilePhones =
          user?.inputName === 'mobilePhones' ? values?.name : currentUser?.mobilePhones;
        const currentEmail = user?.inputName === 'email' ? values?.name : currentUser?.email;

        const data = {
          id: user?.id,
          name: currentUser?.name?.trim(),
          surName: currentUser?.surName?.trim(),
          userName: currentUser?.userName?.trim(),
          mobilePhones: currentMobilePhones
            ?.trim()
            ?.replace(/\)/g, '')
            ?.replace(/\(/g, '')
            ?.replace(/-/g, '')
            ?.replace(/ /g, ''),
          email: currentEmail?.trim(),
        };

        const action = await dispatch(currentUserUpdate(data));

        if (currentUserUpdate.fulfilled.match(action)) {
          successDialog({
            title: <Text t="success" />,
            message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
            onOk: async () => {
              await handleClose();
            },
          });
        } else {
          errorDialog({
            title: <Text t="error" />,
            message: <Text t="transactionNotCompleted" />,
          });
        }
      }
    },
    [handleClose, dispatch, user, currentUserUpdate, currentUser, isOpenPopover],
  );

  const onCancel = useCallback(() => {
    form.setFieldsValue({
      name: user?.name,
    });
    handleModalVisible(false);
    setIsOpenPopover(null);
  }, [user, handleModalVisible, form, setIsOpenPopover]);

  const onChangePopover = useCallback(
    (val) => {
      if (user?.inputName === 'mobilePhones') {
        const value = val
          ?.trim()
          ?.replace(/\)/g, '')
          ?.replace(/\(/g, '')
          ?.replace(/-/g, '')
          ?.replace(/ /g, '');

        if (value === user?.name) {
          setIsOpenPopover(true);
        } else {
          setIsOpenPopover(false);
        }
      } else if (user?.inputName === 'email') {
        const value = val?.trim();

        if (value === user?.name) {
          setIsOpenPopover(true);
        } else {
          setIsOpenPopover(false);
        }
      }
    },
    [setIsOpenPopover, user],
  );

  return (
    <CustomModal
      className="profile-user-phone-or-email-model"
      maskClosable={false}
      footer={false}
      title={<Header title={headerTitle} type={'warning'} icon={modalWarning}></Header>}
      visible={modalVisible}
      onCancel={handleClose}
      closeIcon={<CustomImage src={modalClose} />}
    >
      {user?.inputName === 'mobilePhones' && (
        <>
          <Paragraph>
            <Text t="youHaveSuccessfullyVerifiedYourMobilePhoneRegistered" />
          </Paragraph>
        </>
      )}

      {user?.inputName === 'email' && (
        <>
          <Paragraph>
            <Text t="youHaveSuccessfullyVerifiedYourEMailAddress" />
          </Paragraph>
        </>
      )}
      <CustomForm
        name="personalInformation"
        onFinish={onFinishEdit}
        form={form}
        initialValues={{
          name: user?.name,
        }}
        awaitLoading={!user?.id}
        autoComplete="off"
        layout={'vertical'}
      >
        <div className="row ">
          <div className="col-md-12" style={{ marginBottom: '10px' }}>
            {user?.inputName === 'mobilePhones' && (
              <CustomFormItem
                label={<Text t="phoneNumber" />}
                style={{ marginBottom: '3px' }}
                name="name"
                rules={[
                  { required: true, message: <Text t="pleaseEnterPhone" /> },
                  { validator: formPhoneRegex, message: <Text t="enterValidPhoneNumber" /> },
                ]}
              >
                <CustomMaskInput
                  className={isOpenPopover === true ? 'ant-form-item-profile' : 'aa'}
                  id="myProfile-personalInformation-mobilePhones-maskInput"
                  mask={'+\\90 (999) 999 99 99'}
                  onChange={(e) => onChangePopover(e.target.value)}
                >
                  <CustomInput
                    id="myProfile-personalInformation-mobilePhones-input"
                    data-testid="mobilePhones"
                    // 2. Use the pre-calculated variable here
                    placeholder={phonePlaceholder}
                  />
                </CustomMaskInput>
              </CustomFormItem>
            )}

            {user?.inputName === 'mobilePhones' && isOpenPopover === true && (
              <span className="ant-form-item-explain ant-form-item-explain-error">
                <Text t="pleaseDoNotUseARegisteredMobileNumber" />
              </span>
            )}

            {user?.inputName === 'email' && (
              <CustomFormItem
                label={<Text t="emailAdress" />}
                style={{ marginBottom: '3px' }}
                name="name"
                rules={[
                  { required: true, message: <Text t="pleaseEnterEmail" /> },
                  { validator: formMailRegex, message: <Text t="enterValidEmail" /> },
                ]}
              >
                <CustomInput
                  id="myProfile-personalInformation-email-input"
                  data-testid="email"
                  // 2. Use the pre-calculated variable here
                  placeholder={emailPlaceholder}
                  onChange={(e) => onChangePopover(e.target.value)}
                />
              </CustomFormItem>
            )}

            {user?.inputName === 'email' && isOpenPopover === true && (
              <span className="ant-form-item-explain ant-form-item-explain-error">
                <Text t="pleaseDoNotUseARegisteredEmailAddress" />
              </span>
            )}
          </div>
        </div>

        <CustomFormItem className="custom-form-footer" style={{ paddingTop: '20px' }}>
          <CustomButton
            id="myProfile-personalInformation-custom-btn-cancel"
            data-testid="cancel"
            className="custom-btn-cancel"
            onClick={onCancel}
          >
            <span className="submit">
              <Text t="giveUp" />
            </span>
          </CustomButton>
          <CustomButton
            id="myProfile-personalInformation-custom-btn-primary"
            data-testid="submit"
            className="custom-btn-primary"
            type="primary"
            htmlType="submit"
          >
            <span className="submit">
              <Text t="continue" />
            </span>
          </CustomButton>
        </CustomFormItem>
      </CustomForm>
    </CustomModal>
  );
};

NewPhoneOrEmailFormModal.propTypes = {
  modalVisible: PropTypes.bool,
  handleModalVisible: PropTypes.func,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inputName: PropTypes.string,
    name: PropTypes.string,
  }),
  currentUser: PropTypes.shape({
    name: PropTypes.string,
    surName: PropTypes.string,
    userName: PropTypes.string,
    mobilePhones: PropTypes.string,
    email: PropTypes.string,
  }),
};

export default NewPhoneOrEmailFormModal;